import { $fetch } from 'ofetch';
import type { EventProps } from '~/types/Production';
import { useFavourites } from '~/composables/useFavourites';
import { useAuth } from '~/composables/useAuth';

// Note: /api/cio proxy is configured in server/api/[...].ts (catchAll route)
export type TickslyFavouriteEvent = Pick<EventProps, 'SyncId'>;

const addEventToFavourites = async (SyncId: string) => {
  const favouritesState = useFavourites();
  const alreadyPresent = favouritesState.value.favourites.find((f) => f === SyncId);
  if (alreadyPresent) {
    console.warn('Trying to add event to favourites but it already is there');
    return;
  }

  try {
    const tickslyFavouriteEvents = [...favouritesState.value.favourites, SyncId];
    await saveAttributes({ tickslyFavouriteEvents });
    storeFavouritesToState(tickslyFavouriteEvents);
  } catch (error) {
    console.error('Failed to add event to favourites:', error);
    throw error;
  }
};

const removeEventFromFavourites = async (SyncId: string) => {
  const favouritesState = useFavourites();

  try {
    const tickslyFavouriteEvents = favouritesState.value.favourites.filter((f) => f !== SyncId);
    await saveAttributes({ tickslyFavouriteEvents });
    storeFavouritesToState(tickslyFavouriteEvents);
  } catch (error) {
    console.error('Failed to remove event from favourites:', error);
    throw error;
  }
};

const storeFavouritesToState = (favourites: string[]) => {
  const favouritesState = useFavourites();
  favouritesState.value.favourites = favourites;
};

const loadFavourites = async () => {
  try {
    const currentAttributes = await getCustomerAttributes();
    const tickslyFavouriteEvents = JSON.parse(
      currentAttributes.tickslyFavouriteEvents || '[]',
    ) as string[];

    storeFavouritesToState(tickslyFavouriteEvents);
    return tickslyFavouriteEvents;
  } catch (error) {
    console.error('Failed to load favourites:', error);

    // Always ensure we have a valid state, even on error
    const fallbackFavorites: string[] = [];
    storeFavouritesToState(fallbackFavorites);
    return fallbackFavorites;
  }
};

const getUserEmail = (): string => {
  // Try sessionStorage first
  let email = window.sessionStorage.getItem('email');

  // If not in sessionStorage, try to get from auth
  if (!email) {
    const auth = useAuth();
    email = auth.user.value?.email;
  }

  if (!email) {
    throw new Error('User email not found in sessionStorage or auth state');
  }

  return email;
};

// Note: if not found, it will create an identity
const getCustomerAttributes = async (): Promise<Record<string, any>> => {
  const favouritesState = useFavourites();

  try {
    const email = getUserEmail();
    favouritesState.value.isLoading = true;

    // https://customer.io/docs/api/app/?region=eu#operation/getPersonAttributes
    const response = await $fetch(`/api/cio/customers/${email}/attributes`, {
      query: { id_type: 'email' },
    });

    favouritesState.value.isLoading = false;
    const attributes = response?.customer?.attributes;

    if (!attributes) {
      console.error('Received an empty attributes response from customerIo', response);
      return getDefaultAttributes();
    }

    return attributes;
  } catch (error: any) {
    favouritesState.value.isLoading = false;

    // Handle 404 specifically - user doesn't exist in Customer.io
    if (error?.status === 404 || error?.statusCode === 404) {
      console.log('Customer not found in Customer.io, will create new identity');

      return await handleMissingCustomer();
    }

    // For other errors, log but don't throw - return safe fallback
    console.error('Customer.io API error (non-404):', error);
    return getDefaultAttributes();
  }
};

const handleMissingCustomer = async (): Promise<Record<string, any>> => {
  try {
    const email = getUserEmail();
    const language = useState('language');

    const initialAttributes = getDefaultAttributes();

    // Create the customer identity
    await createCustomerIdentity(email, {
      ...initialAttributes,
      language_code: String(language.value),
      created_at: new Date().toISOString(),
      source: 'profile_page',
    });

    // Retry with backoff strategy
    const maxRetries = 3;
    const baseDelay = 1000; // 1 second

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        // Exponential backoff: 1s, 2s, 4s
        const delay = baseDelay * Math.pow(2, attempt - 1);
        await new Promise((resolve) => setTimeout(resolve, delay));

        console.log(`Attempting to fetch customer attributes (attempt ${attempt}/${maxRetries})`);

        const response = await $fetch(`/api/cio/customers/${email}/attributes`, {
          query: { id_type: 'email' },
        });

        if (response?.customer?.attributes) {
          console.log('Successfully fetched customer attributes after creation');
          return response.customer.attributes;
        }
      } catch (retryError: any) {
        console.warn(`Retry attempt ${attempt} failed:`, retryError?.status || retryError?.message);

        // If it's still a 404 and we're not on the last attempt, continue retrying
        if (
          (retryError?.status === 404 || retryError?.statusCode === 404) &&
          attempt < maxRetries
        ) {
          continue;
        }

        // If it's a different error or we've exhausted retries, break
        if (attempt === maxRetries) {
          console.warn('All retry attempts exhausted, using fallback attributes');
        }
      }
    }

    // If all retries failed, return the initial attributes as fallback
    console.log('Using initial attributes as fallback after failed retries');
    return initialAttributes;
  } catch (createError) {
    console.error('Failed to create new customer identity:', createError);
    return getDefaultAttributes();
  }
};

const getDefaultAttributes = (): Record<string, any> => {
  const language = useState('language');
  return {
    tickslyFavouriteEvents: JSON.stringify([]),
    language_code: String(language.value),
    created_at: new Date().toISOString(),
    source: 'profile_page',
  };
};

// Updated createCustomerIdentity with better error handling
const createCustomerIdentity = async (email: string, attributes: Record<string, any>) => {
  console.log('Creating Customer.io identity for:', email);

  try {
    const response = await $fetch(`/api/cio-track/v2/entity`, {
      method: 'POST',
      body: {
        type: 'person',
        identifiers: {
          email,
        },
        action: 'identify',
        attributes,
      },
    });

    console.log('Customer.io identity created successfully');
    return response;
  } catch (error) {
    console.error('Failed to create Customer.io identity:', error);
    throw error;
  }
};

const saveAttributes = async (attributes: Record<string, any> = {}) => {
  try {
    const email = getUserEmail();
    const favouritesState = useFavourites();

    favouritesState.value.isLoading = true;
    await sendIdentifyRequest(email, attributes);
    favouritesState.value.isLoading = false;
  } catch (error) {
    const favouritesState = useFavourites();
    favouritesState.value.isLoading = false;
    console.error('Failed to save attributes:', error);
    throw error;
  }
};

const sendIdentifyRequest = async (email: string, attributes: Record<string, any> = {}) => {
  try {
    // https://customer.io/docs/api/track/#operation/entity
    // note: returns {}
    return await $fetch(`/api/cio-track/v2/entity`, {
      method: 'POST',
      body: {
        type: 'person',
        identifiers: {
          email,
        },
        action: 'identify',
        attributes,
      },
    });
  } catch (error) {
    console.error('Failed to send identify request:', error);
    throw error;
  }
};

const clear = () => {
  const favouritesState = useFavourites();
  favouritesState.value.isLoading = false;
  favouritesState.value.favourites = [];
};

export const customerIoAPI = {
  loadFavourites,
  addEventToFavourites,
  removeEventFromFavourites,
  sendIdentifyRequest,
  clear,
};
